import type { PropertyPhoto } from '@/types/propertyProfile'
import { fetchPlacePhotos } from '@/services/browserPlaces'
import { loadMapsScript, mapsLibraries } from '@/services/googleMapsLoader'

export type BrowserPropertyMedia = {
  formattedAddress: string | null
  lat: number | null
  lng: number | null
  photos: PropertyPhoto[]
  notes: string[]
}

const MAX_PLACE_PHOTOS = 8

const NUMBER_WORDS = new Set(
  ('one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen '
    + 'seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety hundred thousand').split(' '),
)
const ORDINAL_WORDS = new Set(
  ('first second third fourth fifth sixth seventh eighth ninth tenth eleventh twelfth twentieth').split(' '),
)
/** "1st", "22nd": a street's NAME, not a number on it. */
const DIGIT_ORDINAL_RE = /^\d+(?:st|nd|rd|th)$/i

/**
 * Does the address open with a street number? Its first word (split on spaces
 * and commas) carries a digit ("1050", "12A", "91-1234", and the grid numbers of
 * Wisconsin: "N6W23001", "W227N6000"), or is a spelled number or ordinal word
 * ("One Liberty Plaza", "Thirty Hudson Yards", "First National Plaza"). "1st
 * Avenue" does not: that digit is the street's name. A name or a city
 * ("Washington Square, DC", "Charleston, SC") opens with none of these and is
 * not sent to Google at all.
 *
 * This is only a cost short-circuit, and it is the server's
 * `property_media._has_street_number` rule exactly (both are held to
 * `backend/tests/fixtures/street_gate_parity.json`). Whether Google then placed
 * the text on a building is the geocoder's own answer (`isStreetLevel`).
 */
export function hasStreetNumber(address: string): boolean {
  const first = (address ?? '').trim().split(/[\s,]+/, 1)[0] ?? ''
  if (!first) return false
  if (DIGIT_ORDINAL_RE.test(first)) return false
  const word = first.toLowerCase()
  return /\d/.test(first) || NUMBER_WORDS.has(word) || ORDINAL_WORDS.has(word)
}

/** Google `location_type`s that name a building rather than a road's middle or an area. */
const ROOFTOP_LOCATION_TYPES = new Set(['ROOFTOP', 'RANGE_INTERPOLATED'])
/** Result `types` that name an address rather than a place or a road. */
const ADDRESS_RESULT_TYPES = new Set(['street_address', 'premise', 'subpremise'])

/**
 * Is this geocoder answer a point on the building the text names? The server's
 * `geocode_precision.is_street_level`, ported exactly (held to
 * `backend/tests/fixtures/street_gate_parity.json`): ROOFTOP or
 * RANGE_INTERPOLATED, not a partial match (Google substituting the closest
 * address it knows), and typed as an address. A `route` result is a road, not a
 * door, whatever its location_type says. Anything weaker is not shown as the asset.
 */
export function isStreetLevel(geo: {
  geometry?: { location_type?: string | null } | null
  partial_match?: boolean | null
  types?: string[] | null
}): boolean {
  return (
    ROOFTOP_LOCATION_TYPES.has(geo.geometry?.location_type ?? '')
    && !geo.partial_match
    && (geo.types ?? []).some((t) => ADDRESS_RESULT_TYPES.has(t))
  )
}

/**
 * Geocode + Places + Street View still using the browser key (HTTP referrer OK).
 *
 * For a street address only. A name or a city geocodes to the middle of
 * something, and the photos Google holds there are not pictures of the asset.
 * The coordinate it returns is only ever a street-level one (see `isStreetLevel`),
 * and `assetCoordinate` ranks it below the server's and the master's points.
 */
export async function lookupBrowserPropertyMedia(
  apiKey: string,
  address: string,
): Promise<BrowserPropertyMedia> {
  const empty: BrowserPropertyMedia = {
    formattedAddress: null,
    lat: null,
    lng: null,
    photos: [],
    notes: [],
  }
  if (!address.trim()) return empty
  if (!hasStreetNumber(address)) {
    return { ...empty, notes: ['No street number on record, so there is no photo of the building to look up.'] }
  }

  await loadMapsScript(apiKey)
  const libs = await mapsLibraries()
  if (!libs.Geocoder) {
    return { ...empty, notes: ['Browser Geocoder unavailable — enable Geocoding API on the Maps key.'] }
  }

  const geocoder = new libs.Geocoder()
  const geo = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
    void geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]) resolve(results[0])
      else resolve(null)
    })
  })

  if (!geo) {
    return { ...empty, notes: ['Google could not geocode this address in the browser.'] }
  }

  if (!isStreetLevel(geo)) {
    // No coordinate, no Street View, no photos: all three would be of the
    // middle of a place or a road (or of the closest address Google knew),
    // shown as if they were the building.
    return {
      ...empty,
      notes: ['Google placed this address only at street or city level, not on a building, so there is no photo of the building.'],
    }
  }

  const lat = geo.geometry.location.lat()
  const lng = geo.geometry.location.lng()
  const formattedAddress = geo.formatted_address ?? null
  const photos: PropertyPhoto[] = []
  const notes: string[] = []

  const placeId = geo.place_id
  if (placeId) {
    // `null` means neither Places generation would answer — a configuration
    // fact. An empty list means Google genuinely holds no photo of this place.
    // Showing the same empty gallery for both would hide a fixable problem.
    const found = await fetchPlacePhotos(placeId, address, MAX_PLACE_PHOTOS).catch(() => null)
    if (found === null) {
      notes.push(
        'Places is not enabled on the browser Maps key. Enable “Places API (New)” in Google Cloud ' +
          'and allow it on this key.',
      )
    } else if (found.length === 0) {
      notes.push('Google Places returned no photos for this address.')
    } else {
      for (const shot of found) {
        photos.push({ src: shot.src, alt: shot.alt, source: 'Google Places' })
      }
    }
  }

  if (libs.StreetViewService) {
    const loc = new google.maps.LatLng(lat, lng)
    const svs = new libs.StreetViewService()
    const hasStreet = await new Promise<boolean>((resolve) => {
      svs.getPanorama({ location: loc, radius: 80 }, (data, status) => {
        resolve(status === libs.StreetViewStatus.OK && Boolean(data?.location?.pano))
      })
    })
    if (hasStreet) {
      const src = `https://maps.googleapis.com/maps/api/streetview?size=800x450&location=${encodeURIComponent(`${lat},${lng}`)}&key=${encodeURIComponent(apiKey)}`
      photos.push({
        src,
        alt: formattedAddress || address,
        source: 'Street View',
      })
    }
  }

  return { formattedAddress, lat, lng, photos, notes }
}
